import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './TeacherProfile.css';

const TeacherProfile = () => {
  const [teacherData, setTeacherData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const userRole = localStorage.getItem('userRole');
    
    if (!token || userRole !== 'teacher') {
      navigate('/');
      return;
    }

    // Get user data from localStorage
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    
    // In a real app, you would fetch teacher-specific data from your API
    const fetchTeacherData = async () => {
      try {
        // Mock teacher data - replace with actual API call
        const mockTeacherData = {
          ...userData,
          specialization: "Computer Science",
          bio: "PhD in Linguistics with 15+ years of teaching experience. Fluent in 6 languages and passionate about making language learning accessible to all ages.",
          hireDate: "2020-03-15",
          status: "active",
          classes: [
            { id: 1, subject: "English", level: "B2", students: 15, progress: 75 },
            { id: 2, subject: "French", level: "C1", students: 12, progress: 60 },
            { id: 3, subject: "Robotics", level: "Beginner", students: 20, progress: 45 }
          ],
          stats: {
            totalStudents: 47,
            completedClasses: 12,
            satisfactionRate: 98,
            yearsExperience: 15
          }
        };
        
        setTeacherData(mockTeacherData);
      } catch (error) {
        console.error('Error fetching teacher data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTeacherData();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userId');
    localStorage.removeItem('userData');
    navigate('/');
  };

  if (loading) {
    return (
      <div className="teacher-profile-loading">
        <div className="loading-spinner"></div>
        <p>Loading your profile...</p>
      </div>
    );
  }

  if (!teacherData) {
    return (
      <div className="teacher-profile-error">
        <h2>Error loading profile</h2>
        <button onClick={() => navigate('/')}>Return to Home</button>
      </div>
    );
  }

  return (
    <div className="teacher-profile">
      {/* Header */}
      <header className="teacher-header">
        <div className="container">
          <div className="header-content">
            <div className="logo">
              <h1>AMALIKA WORLD</h1>
              <span>Teacher Portal</span>
            </div>
            <nav className="teacher-nav">
              <button 
                className={`nav-btn ${activeTab === 'profile' ? 'active' : ''}`}
                onClick={() => setActiveTab('profile')}
              >
                <i className="fas fa-user"></i>
                Profile
              </button>
              <button 
                className={`nav-btn ${activeTab === 'classes' ? 'active' : ''}`}
                onClick={() => setActiveTab('classes')}
              >
                <i className="fas fa-chalkboard-teacher"></i>
                My Classes
              </button>
              <button 
                className={`nav-btn ${activeTab === 'schedule' ? 'active' : ''}`}
                onClick={() => setActiveTab('schedule')}
              >
                <i className="fas fa-calendar-alt"></i>
                Schedule
              </button>
              <button className="logout-btn" onClick={handleLogout}>
                <i className="fas fa-sign-out-alt"></i>
                Logout
              </button>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="teacher-main">
        <div className="container">
          {/* Profile Overview */}
          <section className="profile-overview">
            <div className="profile-card">
              <div className="profile-header">
                <div className="avatar-section">
                  <div className="teacher-avatar">
                    <img 
                      src={teacherData.profile_picture || '/default-avatar.jpg'} 
                      alt={`${teacherData.first_name} ${teacherData.last_name}`}
                    />
                  </div>
                  <div className="avatar-badge">
                    <i className="fas fa-certificate"></i>
                  </div>
                </div>
                <div className="profile-info">
                  <h1 className="teacher-name">
                    {teacherData.first_name} {teacherData.last_name}
                  </h1>
                  <p className="teacher-role">{teacherData.specialization}</p>
                  <div className="teacher-stats">
                    <div className="stat">
                      <strong>{teacherData.stats.totalStudents}</strong>
                      <span>Students</span>
                    </div>
                    <div className="stat">
                      <strong>{teacherData.stats.completedClasses}</strong>
                      <span>Classes</span>
                    </div>
                    <div className="stat">
                      <strong>{teacherData.stats.yearsExperience}+</strong>
                      <span>Years</span>
                    </div>
                    <div className="stat">
                      <strong>{teacherData.stats.satisfactionRate}%</strong>
                      <span>Rating</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="profile-details">
                <div className="detail-item">
                  <i className="fas fa-envelope"></i>
                  <div>
                    <label>Email</label>
                    <span>{teacherData.email}</span>
                  </div>
                </div>
                <div className="detail-item">
                  <i className="fas fa-phone"></i>
                  <div>
                    <label>Phone</label>
                    <span>{teacherData.phone1}</span>
                  </div>
                </div>
                <div className="detail-item">
                  <i className="fas fa-map-marker-alt"></i>
                  <div>
                    <label>Location</label>
                    <span>{teacherData.state}</span>
                  </div>
                </div>
                <div className="detail-item">
                  <i className="fas fa-calendar"></i>
                  <div>
                    <label>Member Since</label>
                    <span>{new Date(teacherData.hireDate).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Tab Content */}
          <section className="tab-content">
            {activeTab === 'profile' && (
              <div className="profile-tab">
                <div className="bio-section">
                  <h3>About Me</h3>
                  <p>{teacherData.bio}</p>
                </div>
                
                <div className="skills-section">
                  <h3>Specializations</h3>
                  <div className="skills-grid">
                    <div className="skill-card">
                      <i className="fas fa-language"></i>
                      <h4>Language Teaching</h4>
                      <p>English, French, Italian</p>
                    </div>
                    <div className="skill-card">
                      <i className="fas fa-robot"></i>
                      <h4>Robotics</h4>
                      <p>Beginner to Advanced</p>
                    </div>
                    <div className="skill-card">
                      <i className="fas fa-code"></i>
                      <h4>Programming</h4>
                      <p>Python, JavaScript</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'classes' && (
              <div className="classes-tab">
                <h3>My Classes</h3>
                <div className="classes-grid">
                  {teacherData.classes.map((classItem) => (
                    <div key={classItem.id} className="class-card">
                      <div className="class-header">
                        <h4>{classItem.subject} - {classItem.level}</h4>
                        <span className="student-count">
                          {classItem.students} students
                        </span>
                      </div>
                      <div className="progress-section">
                        <div className="progress-bar">
                          <div 
                            className="progress-fill"
                            style={{ width: `${classItem.progress}%` }}
                          ></div>
                        </div>
                        <span className="progress-text">{classItem.progress}% Complete</span>
                      </div>
                      <div className="class-actions">
                        <button className="btn-primary">
                          <i className="fas fa-eye"></i>
                          View Class
                        </button>
                        <button className="btn-secondary">
                          <i className="fas fa-edit"></i>
                          Manage
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'schedule' && (
              <div className="schedule-tab">
                <h3>Teaching Schedule</h3>
                <div className="schedule-placeholder">
                  <i className="fas fa-calendar-plus"></i>
                  <p>Your teaching schedule will appear here</p>
                  <button className="btn-primary">
                    Set Up Schedule
                  </button>
                </div>
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
};

export default TeacherProfile;